import React, { useId, useState, useEffect, useContext } from "react"
import type { ReactNode } from "react"
import cx from "classnames"
import Icon from "../icon"
import type { IconName } from "../icon"
import ButtonGroup, { ButtonGroupContext } from "./button-group"
import IconButton from "./icon-button"
import "./button-styles.css"
import { emit, EVENTS, useTelemetry } from "../event-emitter"
import type { TelemetryValue } from "../event-emitter/telemetry-context"
import { extractText } from "../utils"

const SIZES = {
  sm: "text-sm py-1 px-3 h-8 rounded-sm",
  md: "text-sm py-2 px-4 h-10 rounded",
  lg: "text-sm py-3 px-5 h-12 rounded-lg"
}

const GROUP_SIZES = {
  "horizontal-sm": "h-full px-2 rounded-xs",
  "horizontal-md": "h-full px-3 rounded-sm",
  "horizontal-lg": "h-full px-4 rounded",
  "vertical-sm": "p-1 w-full rounded-xs",
  "vertical-md": "p-1 w-full rounded-sm",
  "vertical-lg": "p-2 w-full rounded"
}

export const TOKENS = {
  primary: "text-white hover:text-white bg-black hover:bg-gray-800",
  "primary-blue":
    "text-gray-900 hover:text-black bg-blue-500 hover:bg-blue-400",
  secondary: "text-gray-900 bg-gray-900/[0.06] hover:bg-gray-900/[0.12]",
  outlined:
    "text-gray-900 bg-transparent !border-gray-200 hover:bg-gray-900/[0.06]",
  tertiary: "text-gray-900 bg-transparent hover:bg-gray-900/[0.06]",
  plain: ""
}

const DISABLED_DEFAULT = "text-gray-600 bg-gray-100"
const DISABLED_TOKENS: Partial<Record<keyof typeof TOKENS, string>> = {
  tertiary: "text-gray-600 bg-transparent",
  outlined: "text-gray-600 bg-transparent !border-gray-200 opacity-40"
}

const COLOR_FALLBACK = {
  red: TOKENS["primary"],
  white: TOKENS["secondary"],
  plain: TOKENS["tertiary"],
  gray: TOKENS["secondary"],
  orange: "text-white bg-orange-500 hover:bg-orange-600",
  blue: "text-gray-900 bg-blue-500 hover:bg-blue-400 hover:text-black focus:text-gray-900",
  green: "text-white bg-green-500 hover:bg-green-600",
  "light-blue": "text-gray-900 bg-blue-200 hover:bg-blue-300",
  "light-green":
    "text-gray-900 bg-green-200 hover:bg-green-300 focus:bg-green-300",
  "light-orange": "text-gray-900 bg-orange-200 hover:bg-orange-300",
  "light-red": "text-gray-900 bg-red-200 hover:bg-red-300"
}

type ButtonPublicProps = {
  /** Custom CSS classes to apply to the button */
  className?: string
  /** Button content */
  children?: ReactNode
  /** Style variant of the button */
  is?: keyof typeof TOKENS
  /** Color variant for specific color styling beyond the standard `is` variants */
  color?: keyof typeof COLOR_FALLBACK
  /** Whether the button is disabled */
  disabled?: boolean
  /** Size of the button */
  size?: keyof typeof SIZES
  /** Icon to display before children */
  icon?: IconName
  /** If provided, renders as an anchor element instead of a button */
  href?: string
  /** Used to manage active state in button group */
  active?: boolean
  /** Handler called when mouse button is pressed down */
  onMouseDown?: (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void
  /** Handler called when button loses focus */
  onBlur?: (event: React.FocusEvent<HTMLButtonElement | HTMLAnchorElement>) => void
  /** Handler called when button is clicked */
  onClick?: (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void
  /** Data to emit alongside the "button_click" event. Set to `false` to disable emission. */
  telemetry?: TelemetryValue
}

export type ButtonProps = ButtonPublicProps &
  Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement> &
      React.AnchorHTMLAttributes<HTMLAnchorElement>,
    keyof ButtonPublicProps
  >

/**
 * A versatile button component that supports multiple style variants, sizes, and states.
 * Can render as a button or anchor element depending on whether an href is provided.
 */
const Button = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>(
  (
    {
      is,
      color,
      size = "md",
      icon,
      disabled = false,
      className,
      children,
      onMouseDown = () => {},
      onBlur = () => {},
      onClick,
      active,
      telemetry,
      ...props
    },
    ref
  ) => {
    const id = useId()
    const groupContext = useContext(ButtonGroupContext)
    const telemetryPayload = useTelemetry(telemetry)
    const [clicked, setClicked] = useState(false)
    const DEFAULT = `leading-6 border border-solid border-transparent no-underline font-sans font-semibold transition duration-200 hover:no-underline focus:no-underline focus:outline-none align-middle text-center inline-flex items-center justify-center`
    const isDisabled =
      disabled &&
      (Object.keys(TOKENS).includes(is as string) ||
        color ||
        groupContext.present)
    const usesTokenStyles =
      Object.keys(TOKENS).includes(is as string) || isDisabled || color
    const isCurrentActiveGroupMember = groupContext.activeId === id || undefined
    const classes = cx(
      className,
      disabled ? "cursor-default" : "cursor-pointer",
      isDisabled &&
        (DISABLED_TOKENS[is as keyof typeof TOKENS] ?? DISABLED_DEFAULT),
      {
        [DEFAULT]: usesTokenStyles || groupContext.present,
        [SIZES[size]]: usesTokenStyles && !groupContext.present,
        [color ? COLOR_FALLBACK[color] : ""]:
          color && !isDisabled && !groupContext.present,
        [is ? TOKENS[is] : ""]: is && !isDisabled && !groupContext.present,
        "focus:outline-blue": !clicked && !groupContext.present,
        [GROUP_SIZES[groupContext.groupSize as keyof typeof GROUP_SIZES]]:
          groupContext.present && (size as string) !== "icon",
        "h-full aspect-square":
          groupContext.present && (size as string) === "icon",
        "hover:bg-gray-900/[0.06]":
          groupContext.present && !isDisabled && !isCurrentActiveGroupMember,
        "bg-white": isCurrentActiveGroupMember
      }
    )

    const { setActiveId } = groupContext
    useEffect(() => {
      if (active && setActiveId) {
        setActiveId(id)
      }
    }, [active, setActiveId, id])

    const handleMouseDown = (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
      setClicked(true)
      onMouseDown(event)
    }

    const handleBlur = (event: React.FocusEvent<HTMLButtonElement | HTMLAnchorElement>) => {
      setClicked(false)
      onBlur(event)
    }

    const handleClick = (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
      if (setActiveId) {
        setActiveId(id)
      }

      if (telemetryPayload !== false) {
        emit(EVENTS.BUTTON_CLICK, {
          text: extractText(children),
          ...telemetryPayload
        })
      }

      if (onClick) onClick(event)
    }

    const Component = props.href ? "a" : "button"

    return (
      <Component
        {...props}
        ref={ref as React.Ref<HTMLButtonElement & HTMLAnchorElement>}
        className={classes}
        disabled={disabled}
        onMouseDown={handleMouseDown}
        onBlur={handleBlur}
        onClick={handleClick}
        aria-pressed={isCurrentActiveGroupMember}
      >
        {icon && (
          <Icon
            name={icon}
            className={cx("-ml-1 pointer-events-none", {
              "mr-2": Boolean(children)
            })}
            aria-hidden={children ? "true" : "false"}
          />
        )}
        {children}
      </Component>
    )
  }
)

export default Button

export { Button, ButtonGroup, IconButton }
